import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import * as signalR from "@microsoft/signalr";
import { useUserContext } from "./UserContext";
import { isAbortError } from "../services/fetchService";
import { fetchMarcarNotificacionLeida, fetchNotificacionesPendientes, NOTIFICACION_TIPOS, NotificacionDto, NotificacionUnauthorizedError } from "../services/notificacionService";

const URLAPI = process.env.REACT_APP_BASE_URL ?? "";
const HUB_URL = URLAPI.replace(/\/api\/?$/i, "") + "/notificacionesHub";

const notificationPermissionByType: Record<string, string> = {
  [NOTIFICACION_TIPOS.PEDIDO_WEB]: "pedidos_clientes",
  [NOTIFICACION_TIPOS.VENTA_WEB]: "orden_venta",
};

type NotificationOptions = {
  autoHideDuration?: number;
};

type NotificationState = {
  open: boolean;
  message: string;
  severity: AlertColor;
  autoHideDuration: number;
};

type NotificationContextType = {
  showNotification: (message: string, severity?: AlertColor, options?: NotificationOptions) => void;
  hideNotification: () => void;
  notifications: NotificacionDto[];
  unreadCount: number;
  refreshNotifications: () => Promise<void>;
  markNotificationRead: (idNotificacion: number) => Promise<void>;
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }

  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useUserContext();
  const isEmployeeUser = Boolean(
    user?.token &&
    !user?.idCliente &&
    (Number(user?.idEmpleado ?? 0) > 0 || (user?.permissions?.length ?? 0) > 0)
  );
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: "",
    severity: "info",
    autoHideDuration: 6000,
  });
  const [notifications, setNotifications] = useState<NotificacionDto[]>([]);
  const notifiedIdsRef = useRef<Set<number>>(new Set());
  const canSeeNotification = useCallback((item: NotificacionDto) => {
    const requiredPermission = notificationPermissionByType[item.tipo];
    return !requiredPermission || user?.permissions?.includes(requiredPermission);
  }, [user?.permissions]);

  const showNotification = useCallback((message: string, severity: AlertColor = "info", options?: NotificationOptions) => {
    setNotification({
      open: true,
      message,
      severity,
      autoHideDuration: options?.autoHideDuration ?? 6000,
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((current) => ({ ...current, open: false }));
  }, []);

  const refreshNotifications = useCallback(async (signal?: AbortSignal) => {
    if (!isEmployeeUser || !user) {
      setNotifications([]);
      return;
    }

    const items = await fetchNotificacionesPendientes(user, 0, 20, signal);
    setNotifications(items);

    const latest = items[0];
    if (latest && !notifiedIdsRef.current.has(latest.idNotificacion)) {
      notifiedIdsRef.current.add(latest.idNotificacion);
      showNotification(latest.mensaje || latest.titulo, "info");
    }
  }, [isEmployeeUser, showNotification, user]);

  const markNotificationRead = useCallback(async (idNotificacion: number) => {
    if (!isEmployeeUser || !user) return;
    await fetchMarcarNotificacionLeida(user, idNotificacion);
    setNotifications((current) => current.filter((item) => item.idNotificacion !== idNotificacion));
  }, [isEmployeeUser, user]);

  const receiveRealtimeNotification = useCallback((item: NotificacionDto) => {
    if (!item?.idNotificacion) return;
    if (!canSeeNotification(item)) return;

    setNotifications((current) => {
      if (current.some((notificationItem) => notificationItem.idNotificacion === item.idNotificacion)) {
        return current;
      }

      return [item, ...current].slice(0, 20);
    });

    if (!notifiedIdsRef.current.has(item.idNotificacion)) {
      notifiedIdsRef.current.add(item.idNotificacion);
      showNotification(item.mensaje || item.titulo, "info");
    }
  }, [canSeeNotification, showNotification]);

  useEffect(() => {
    if (!isEmployeeUser) {
      setNotifications([]);
      return;
    }

    const controller = new AbortController();
    refreshNotifications(controller.signal).catch((error) => {
      if (!isAbortError(error) && !(error instanceof NotificacionUnauthorizedError)) console.log(error);
    });

    const intervalId = window.setInterval(() => {
      refreshNotifications().catch((error) => {
        if (!isAbortError(error) && !(error instanceof NotificacionUnauthorizedError)) console.log(error);
      });
    }, 30000);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [isEmployeeUser, refreshNotifications]);

  useEffect(() => {
    if (!isEmployeeUser || !user || !HUB_URL) {
      return;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_URL}?clientType=web-empleado`, {
        accessTokenFactory: () => user.token ?? "",
      })
      .withAutomaticReconnect()
      .build();

    connection.on("NotificacionCreada", receiveRealtimeNotification);
    connection.start().catch((error) => console.log(error));

    return () => {
      connection.off("NotificacionCreada", receiveRealtimeNotification);
      connection.stop().catch((error) => console.log(error));
    };
  }, [isEmployeeUser, receiveRealtimeNotification, user]);

  const unreadCount = notifications.length;

  const value = useMemo(
    () => ({ showNotification, hideNotification, notifications, unreadCount, refreshNotifications: () => refreshNotifications(), markNotificationRead }),
    [hideNotification, markNotificationRead, notifications, refreshNotifications, showNotification, unreadCount]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.autoHideDuration}
        onClose={hideNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={hideNotification} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
